import gsap from 'gsap';
import Point from './point';
import { PolygonCorner } from '../typs/polygon-corner';

type EventType = 'onUpdate' | 'onComplete';
type EventCallback = (event: CustomEvent) => void;
type AnimateOptions = {
  point: Point;
  duration?: number;
  delay?: number;
  ease?: string;
};

class ClipPathPolygon {
  public TL!: Point;
  public TR!: Point;
  public BR!: Point;
  public BL!: Point;
  private eventListeners: Map<EventType, EventCallback[]> = new Map();

  constructor(TL: Point, TR: Point, BR: Point, BL: Point) {
    this.TL = TL;
    this.TR = TR;
    this.BR = BR;
    this.BL = BL;
  }

  public setPoint(key: PolygonCorner, value: Point): void {
    this[key] = value;
  }

  public animate(key: PolygonCorner, options: AnimateOptions): void {
    const { point, duration = 0.5, delay = 0, ease = 'power2.out' } = options;

    const tl = gsap.to(this[key], {
      duration,
      ease,
      x: point.x,
      y: point.y,
      delay,
      onUpdate: () => {
        const progress = tl.progress();
        this.dispatchEvent('onUpdate', { progress });
      },
      onComplete: () => {
        this.dispatchEvent('onComplete');
      },
    });
  }

  public addEventListener(type: EventType, callback: EventCallback): void {
    const listeners = this.eventListeners.get(type) || [];
    this.eventListeners.set(type, [...listeners, callback.bind(this)]);
  }

  public removeEventListener(type: EventType, callback: EventCallback): void {
    const listeners = this.eventListeners.get(type) || [];
    this.eventListeners.set(
      type,
      listeners.filter((listener) => listener !== callback)
    );
  }

  private dispatchEvent(type: EventType, data?: any): void {
    const listeners = this.eventListeners.get(type);
    if (!listeners?.length) return;

    const event = new CustomEvent(type, {
      detail: {
        clipPath: this.getClipPath(),
        ...data,
      },
    });

    listeners.forEach((callback) => callback(event));
  }

  public getClipPath(): string {
    return `polygon(${this.TL.x}% ${this.TL.y}%, ${this.TR.x}% ${this.TR.y}%, ${this.BR.x}% ${this.BR.y}%, ${this.BL.x}% ${this.BL.y}%)`;
  }
}

export default ClipPathPolygon;
